import {useState, useCallback} from "react";
import {
	GridFilterModel,
	GridSortModel,
	GridInitialState
} from "@mui/x-data-grid-pro";
import {useListQuizzes, type ListQuizItemDto} from "@/app2/shared/data";
import type {QuizzesControllerListParams} from "@/app2/shared/data/gen";

export const useQuizzesListGrid = () => {
	const [filterModel, setFilterModel] = useState<QuizzesControllerListParams>({
		page: 1,
		pageSize: 25,
		orderBy: "title",
		orderDirection: "asc",
		extendedAccess: false
	});

	const {data, isLoading, refetch} = useListQuizzes(filterModel);

	const rows: ListQuizItemDto[] = data?.data ?? [];
	const rowCount = data?.totalCount ?? 0;

	const onPageChange = useCallback((page: number) => {
		setFilterModel((prev) => ({
			...prev,
			page: page + 1
		}));
	}, []);

	const onPageSizeChange = useCallback((pageSize: number) => {
		setFilterModel((prev) => ({
			...prev,
			pageSize,
			page: 1
		}));
	}, []);

	const onFilterChange = useCallback((newFilterModel: GridFilterModel) => {
		setFilterModel((prev) => {
			const updated: QuizzesControllerListParams = {
				...prev,
				page: 1
			};

			const quickFilter = newFilterModel.quickFilterValues?.[0];
			if (quickFilter !== undefined && quickFilter !== null && quickFilter !== "") {
				updated.searchTerm = String(quickFilter).trim();
			} else {
				const titleItem = newFilterModel.items?.find(
					(item: { columnField?: string }) => item.columnField === "title"
				);
				if (
					titleItem &&
					titleItem.value !== undefined &&
					titleItem.value !== null &&
					String(titleItem.value).trim() !== ""
				) {
					updated.searchTerm = String(titleItem.value).trim();
				} else {
					delete updated.searchTerm;
				}
			}

			return updated;
		});
	}, []);

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		setFilterModel((prev) => ({
			...prev,
			orderBy:
				sortModel.length > 0
					? (sortModel[0].field as "title" | "updatedAt")
					: "title",
			orderDirection:
				sortModel.length > 0
					? (sortModel[0].sort as "asc" | "desc")
					: "asc"
		}));
	}, []);

	const defaultInitialState: GridInitialState = {
		pagination: {
			pageSize: filterModel.pageSize ?? 25,
			page: (filterModel.page ?? 1) - 1
		},
		sorting: {
			sortModel: [{field: "title", sort: "asc"}]
		}
	};

	return {
		rows,
		rowCount,
		isLoading,
		refetch,
		page: (filterModel.page ?? 1) - 1,
		pageSize: filterModel.pageSize ?? 25,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		onSortModelChange,
		defaultInitialState
	};
};
